import './Navbar.css';
import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { BRAND_NAME, BRAND_TAGLINE } from '../constants/brand';

const LEFT_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/photos', label: 'Photos' },
  { to: '/films', label: 'Films' },
];

const RIGHT_LINKS = [
  { to: '/packages', label: 'Packages' },
  { to: '/budget-calculator', label: 'Budget' },
  { to: '/book', label: 'Book', cta: true },
];

const ALL_LINKS = [...LEFT_LINKS, ...RIGHT_LINKS];

const Navbar = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  /** True once the homepage hero is no longer in view (or on non-home routes) */
  const [isPastHero, setIsPastHero] = useState(location.pathname !== '/');

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Transparent over .hero-video; navy once it leaves the viewport (pin scrub included)
  useEffect(() => {
    if (location.pathname !== '/') {
      setIsPastHero(true);
      return undefined;
    }

    setIsPastHero(false);

    let observer;
    let framed = 0;

    const attach = () => {
      const hero = document.querySelector('.hero-video');
      if (!hero) {
        // Hero may mount a tick later on home
        if (framed < 40) {
          framed += 1;
          requestAnimationFrame(attach);
        } else {
          setIsPastHero(true);
        }
        return;
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          setIsPastHero(!entry.isIntersecting);
        },
        {
          // Trigger slightly before the hero fully exits under the floating pill
          root: null,
          threshold: 0,
          rootMargin: '-8% 0px 0px 0px',
        }
      );
      observer.observe(hero);
    };

    attach();

    return () => {
      if (observer) observer.disconnect();
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobileMenuOpen]);

  const linkClass = (cta) =>
    ({ isActive }) =>
      [
        'nav-link',
        cta ? 'nav-link--cta' : '',
        isActive ? 'active' : '',
      ]
        .filter(Boolean)
        .join(' ');

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <header
        className={[
          'navbar',
          isPastHero ? 'navbar--past-hero' : 'navbar--on-hero',
          isMobileMenuOpen ? 'navbar--menu-open' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="banner"
      >
        <div className="navbar-pill">
          <div className="navbar-pill-media" aria-hidden="true" />

          <nav className="navbar-pill-inner" role="navigation" aria-label="Main navigation">
            <ul className="navbar-cluster navbar-cluster--left">
              {LEFT_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <NavLink to={to} className={linkClass(false)} onClick={closeMobileMenu}>
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>

            <Link
              to="/"
              className="navbar-brand"
              onClick={closeMobileMenu}
              aria-label={`${BRAND_NAME} Home - ${BRAND_TAGLINE}`}
            >
              <span className="navbar-brand-name">
                <span className="navbar-brand-primary">Khuyung</span>
                <span className="navbar-brand-secondary">WedStories</span>
              </span>
              <span className="navbar-brand-tagline">{BRAND_TAGLINE}</span>
            </Link>

            <ul className="navbar-cluster navbar-cluster--right">
              {RIGHT_LINKS.map(({ to, label, cta }) => (
                <li key={to}>
                  <NavLink to={to} className={linkClass(!!cta)} onClick={closeMobileMenu}>
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>

            <button
              type="button"
              className={`navbar-toggler ${isMobileMenuOpen ? 'navbar-toggler--open' : ''}`}
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="primary-navigation"
            >
              <span className="navbar-toggler-lines" aria-hidden="true">
                <span />
                <span />
              </span>
              <span className="navbar-toggler-caption">
                {isMobileMenuOpen ? 'Close' : 'Menu'}
              </span>
            </button>
          </nav>
        </div>
      </header>

      <div
        id="primary-navigation"
        className={`navbar-drawer ${isMobileMenuOpen ? 'navbar-drawer--open' : ''}`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="navbar-drawer-media" aria-hidden="true" />
        <ul className="navbar-drawer-list">
          {ALL_LINKS.map(({ to, label, cta }) => (
            <li key={to}>
              <NavLink to={to} className={linkClass(!!cta)} onClick={closeMobileMenu}>
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {isMobileMenuOpen && (
        <div className="navbar-backdrop" onClick={closeMobileMenu} aria-hidden="true" />
      )}
    </>
  );
};

export default Navbar;
